import { existsSync } from "node:fs";
import { mkdir, open, readdir, readFile, rename, stat } from "node:fs/promises";
import { join } from "node:path";
import { extractNextUp } from "./agenda.js";

/**
 * Session hand-off store: `data/_handoff/`.
 *
 * A hand-off is a forward-looking baton — "where this session stopped and what
 * the next one should pick up" — kept SEPARATE from the worklog (the permanent,
 * backward-looking record). One file per session, named
 * `YYYY-MM-DD_HHMM.md` (a `-2`/`-3` suffix is added only when two sessions wrap
 * in the same minute, so a concurrent session never clobbers another's baton).
 * The file is created with an EXCLUSIVE open (`wx`) so that collision handling
 * is atomic, not an LLM ritual. Old hand-offs are swept to `_handoff/_archive/`
 * by {@link pruneHandoffs} (deterministic, age-based) — git keeps the history.
 *
 * Design rationale + the alternatives rejected (carry-forward, archive-on-
 * consume): decision-log `2026-06-07-핸드오프-세션단위-분리-자동정리`.
 */
export const HANDOFF_DIR = "_handoff";
export const HANDOFF_ARCHIVE_DIR = "_archive";

/** `YYYY-MM-DD_HHMM.md` or `YYYY-MM-DD_HHMM-2.md` (same-minute collision). */
const HANDOFF_PATTERN = /^(\d{4}-\d{2}-\d{2})_(\d{4})(?:-(\d+))?\.md$/;

/** Don't pull a pathologically large hand-off into memory on the hot path. */
const MAX_HANDOFF_READ_BYTES = 128 * 1024;
/** Backstop so the exclusive-create retry loop can never spin forever. */
const MAX_COLLISION_TRIES = 50;

export interface HandoffWriteResult {
  readonly path: string;
  readonly name: string;
  readonly date: string;
  readonly time: string;
}

/**
 * Create a new, EMPTY-skeleton hand-off file and return its path for the agent
 * to fill in (mirrors `/log`: the host creates the file safely, the agent writes
 * the content). Atomic exclusive create; on a same-minute collision a numeric
 * suffix is appended so a concurrent session's file is never overwritten.
 */
export async function createHandoffSkeleton(
  dataDir: string,
  opts?: { readonly now?: Date; readonly title?: string },
): Promise<HandoffWriteResult> {
  const now = opts?.now ?? new Date();
  const date = isoDate(now);
  const time = isoTime(now);
  const dir = join(dataDir, HANDOFF_DIR);
  await mkdir(dir, { recursive: true });

  const stamp = `${date} ${time.slice(0, 2)}:${time.slice(2)}`;
  const title = (opts?.title ?? "").trim();
  const content = renderHandoffSkeleton(stamp, title);

  const base = `${date}_${time}`;
  for (let i = 0; i < MAX_COLLISION_TRIES; i++) {
    const name = i === 0 ? `${base}.md` : `${base}-${i + 1}.md`;
    const abs = join(dir, name);
    try {
      const fh = await open(abs, "wx");
      try {
        await fh.writeFile(content, "utf8");
      } finally {
        await fh.close();
      }
      return { path: abs, name, date, time };
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "EEXIST") continue;
      throw e;
    }
  }
  throw new Error(`Could not create a unique hand-off file under ${dir} after ${MAX_COLLISION_TRIES} tries`);
}

function renderHandoffSkeleton(stamp: string, title: string): string {
  const heading = title ? `# 핸드오프 · ${stamp} — ${title}` : `# 핸드오프 · ${stamp}`;
  return (
    `---\n` +
    `type: handoff\n` +
    `created: ${stamp}\n` +
    `---\n\n` +
    `${heading}\n\n` +
    `## 다음 작업\n\n` +
    `## 현재 상태\n\n` +
    `## 이어받을 포인터\n`
  );
}

export interface HandoffSummary {
  readonly date: string;
  readonly time: string;
  /** Path relative to the data dir, e.g. `_handoff/2026-06-07_1604.md`. */
  readonly relPath: string;
  /** First `# ` heading, else the filename. RAW — the caller sanitizes. */
  readonly title: string;
  /** Hand-off `## 다음 작업` lines (via {@link extractNextUp}). RAW. */
  readonly nextUp: readonly string[];
}

/**
 * List the ACTIVE hand-offs (everything under `_handoff/` except `_archive/`),
 * newest first. Returns up to `max` summaries with the count omitted beyond it,
 * so a busy stretch of many small hand-offs stays a bounded pointer at session
 * start. Multiple active hand-offs are NORMAL (concurrent sessions) — the caller
 * surfaces them all, not as an exceptional state.
 */
export async function scanHandoffs(
  dataDir: string,
  opts?: { readonly max?: number },
): Promise<{ active: HandoffSummary[]; omitted: number }> {
  const dir = join(dataDir, HANDOFF_DIR);
  if (!existsSync(dir)) return { active: [], omitted: 0 };
  let names: string[];
  try {
    names = await readdir(dir);
  } catch {
    return { active: [], omitted: 0 };
  }

  const matched = names
    .map((name) => ({ name, m: name.match(HANDOFF_PATTERN) }))
    .filter((x): x is { name: string; m: RegExpMatchArray } => x.m !== null)
    .sort((a, b) => (a.name < b.name ? 1 : a.name > b.name ? -1 : 0)); // newest first

  const max = opts?.max ?? 6;
  const take = matched.slice(0, max);
  const omitted = Math.max(0, matched.length - take.length);

  const active: HandoffSummary[] = [];
  for (const { name, m } of take) {
    const abs = join(dir, name);
    let title = name.replace(/\.md$/, "");
    let nextUp: string[] = [];
    try {
      if ((await stat(abs)).size <= MAX_HANDOFF_READ_BYTES) {
        const raw = await readFile(abs, "utf8");
        const h = raw.match(/^#\s+(.+)$/m);
        if (h) title = h[1]!.trim();
        nextUp = extractNextUp(raw);
      }
    } catch {
      // unreadable — keep the filename-derived title, no nextUp
    }
    active.push({ date: m[1]!, time: m[2]!, relPath: `${HANDOFF_DIR}/${name}`, title, nextUp });
  }
  return { active, omitted };
}

/**
 * Sweep hand-offs older than `retentionDays` into `_handoff/_archive/`
 * (deterministic, age-based — NOT an LLM step, so it cannot drift). Idempotent
 * and concurrency-tolerant: a file already moved by a racing session is simply
 * skipped. Git keeps the full history, so this is a working-tree tidy, not a
 * destructive delete. Returns how many were archived.
 */
export async function pruneHandoffs(
  dataDir: string,
  opts: { readonly now?: Date; readonly retentionDays: number },
): Promise<{ archived: number }> {
  const now = opts.now ?? new Date();
  const retentionDays = opts.retentionDays;
  const dir = join(dataDir, HANDOFF_DIR);
  if (!existsSync(dir) || !(retentionDays > 0)) return { archived: 0 };
  let names: string[];
  try {
    names = await readdir(dir);
  } catch {
    return { archived: 0 };
  }

  const cutoff = isoDate(addDays(startOfDay(now), -retentionDays));
  const stale = names
    .map((name) => ({ name, m: name.match(HANDOFF_PATTERN) }))
    .filter((x): x is { name: string; m: RegExpMatchArray } => x.m !== null && x.m[1]! < cutoff);
  if (stale.length === 0) return { archived: 0 };

  const archiveDir = join(dir, HANDOFF_ARCHIVE_DIR);
  await mkdir(archiveDir, { recursive: true });
  let archived = 0;
  for (const { name } of stale) {
    const from = join(dir, name);
    let to = join(archiveDir, name);
    // Never clobber an already-archived hand-off: if the name is taken, append
    // an incrementing suffix until a free one is found.
    if (existsSync(to)) {
      const stem = name.replace(/\.md$/, "");
      let i = 2;
      while (existsSync(join(archiveDir, `${stem}-${i}.md`))) i++;
      to = join(archiveDir, `${stem}-${i}.md`);
    }
    try {
      await rename(from, to);
      archived++;
    } catch {
      // already moved by a racing session, or unreadable — skip
    }
  }
  return { archived };
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
